import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "../../../utils/cmsAxios";
import BusinessFilters from "./Components/BusinessFilters";
import BusinessItemRow from "./Components/BusinessItemRow";
import RejectModal from "./Components/RejectModal";

const API = process.env.REACT_APP_FASTAPI_ADS_URL;

const BusinessList = () => {
    // ✅ 새 필터 상태 (사업주/등록번호/제출상태/등록일자/확인일자)
    const [businessName, setBusinessName] = useState("");
    const [businessNumber, setBusinessNumber] = useState("");
    const [status, setStatus] = useState("");              // pending | approved | rejected | ""
    const [createdDate, setCreatedDate] = useState("");    // YYYY-MM-DD
    const [reviewedDate, setReviewedDate] = useState("");  // YYYY-MM-DD

    const [page, setPage] = useState(1);
    const pageSize = 10;                                   // ✅ 고정

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    const [actionLoading, setActionLoading] = useState(false);
    const [rejectTarget, setRejectTarget] = useState(null);
    const [rejectNotes, setRejectNotes] = useState("");

    const maxPage = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

    // 목록 조회
    const fetchList = useCallback(async () => {
        try {
            setLoading(true);
            setErr("");
            const params = { page, page_size: pageSize };

            if (businessName.trim()) params.business_name = businessName.trim();
            if (businessNumber.trim()) params.business_number = businessNumber.trim();
            if (status) params.status = status;
            if (createdDate) params.created_date = createdDate;
            if (reviewedDate) params.reviewed_date = reviewedDate;

            const { data } = await axios.get(`${API}/cms/verification/list`, { params });
            if (data?.ok) {
                setItems(data.items || []);
                setTotal(data.total ?? 0);
            } else {
                setErr("목록을 불러오지 못했습니다.");
            }
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message || "오류가 발생했습니다.");
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, businessName, businessNumber, status, createdDate, reviewedDate]);

    useEffect(() => { fetchList(); }, [fetchList]);

    // 검색/초기화
    const onSearch = (e) => { e.preventDefault(); setPage(1); };
    const onReset = () => {
        setBusinessName("");
        setBusinessNumber("");
        setStatus("");
        setCreatedDate("");
        setReviewedDate("");
        setPage(1);
    };

    // 승인 / 반려
    const onApprove = async (id) => {
        try {
            setActionLoading(true);
            await axios.post(`${API}/cms/verification/approve/${id}`);
            await fetchList();
        } catch (e) {
            alert(e?.response?.data?.detail || e.message || "승인 처리 중 오류");
            if (e?.response?.status === 409) fetchList();
        } finally {
            setActionLoading(false);
        }
    };

    const openReject = (item) => {
        setRejectTarget(item);
        setRejectNotes(item?.notes || "");
    };
    const onRejectConfirm = async () => {
        if (!rejectTarget) return;
        try {
            setActionLoading(true);
            await axios.post(`${API}/cms/verification/reject/${rejectTarget.id}`, { notes: rejectNotes });
            setRejectTarget(null);
            setRejectNotes("");
            await fetchList();
        } catch (e) {
            alert(e?.response?.data?.detail || e.message || "반려 처리 중 오류");
            if (e?.response?.status === 409) fetchList();
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-neutral-50">
            <div className="mx-auto max-w-[1280px] bg-white rounded-2xl shadow-[0_0_0_1px_rgba(0,0,0,0.06),0_40px_80px_-24px_rgba(0,0,0,0.12)] overflow-hidden">
                <div className="flex">
                    <div className="flex-1 min-w-0">
                        <main className="pt-12 flex flex-col gap-4 p-4 overflow-x-hidden w-full">
                            <section className="p-4">
                                <h2 className="text-xl font-semibold mb-4">사업자등록증 제출 현황</h2>

                                {/* ✅ 새 필터 props 적용 */}
                                <BusinessFilters
                                    businessName={businessName} setBusinessName={setBusinessName}
                                    businessNumber={businessNumber} setBusinessNumber={setBusinessNumber}
                                    status={status} setStatus={setStatus}
                                    createdDate={createdDate} setCreatedDate={setCreatedDate}
                                    reviewedDate={reviewedDate} setReviewedDate={setReviewedDate}
                                    onSearch={onSearch} onReset={onReset}
                                    total={total}
                                />

                                {loading && <p className="text-sm text-gray-500">불러오는 중...</p>}
                                {err && <p className="text-sm text-red-500">{err}</p>}
                                {!loading && !err && items.length === 0 && <p className="text-sm text-gray-500">데이터가 없습니다.</p>}

                                {!loading && !err && items.length > 0 && (
                                    <BusinessItemRow
  items={items}
  apiBase={API}
  onApprove={onApprove}
  onReject={openReject}
  actionLoading={actionLoading}
/>
                                )}

                                {!loading && !err && maxPage > 1 && (
                                    <div className="mt-4 flex items-center justify-center gap-2">
                                        <button
                                            className="px-3 py-1 border rounded disabled:opacity-40"
                                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                                            disabled={page <= 1}
                                        >이전</button>
                                        <span className="text-sm text-gray-700">{page} / {maxPage}</span>
                                        <button
                                            className="px-3 py-1 border rounded disabled:opacity-40"
                                            onClick={() => setPage((p) => Math.min(maxPage, p + 1))}
                                            disabled={page >= maxPage}
                                        >다음</button>
                                    </div>
                                )}
                            </section>
                        </main>
                    </div>
                </div>
            </div>

            <RejectModal
                open={!!rejectTarget}
                filename={rejectTarget?.original_filename}
                notes={rejectNotes}
                setNotes={setRejectNotes}
                onClose={() => { setRejectTarget(null); setRejectNotes(""); }}
                onConfirm={onRejectConfirm}
                loading={actionLoading}
            />
        </div>
    );
};

export default BusinessList;
